import React, {
  useEffect,
  useState,
} from "react";

import {
  Stack,
  Text,
  Select,
  Box,
  Image,
} from "@chakra-ui/react";

import ReusableDrawer from "components/Drawers/ReusableDrawer";
import ReusableInput from "components/Inputs/ReusableInput";
import GradientButton from "components/Buttons/GradientButton";

import StatusBadge, {
  getStatusVariant,
} from "../../../../components/Badges/StatusBadge";

import { useDispatch } from "react-redux";

import {
  updateStarterKitPaymentProofAction,
  fetchStarterKitPaymentProofsAction,
} from "state/actions/starterKitPurchaseActions";

import { useCustomNotification } from "components/Toasts/OpenNotification";


const UpdatePaymentProofDrawer = ({
  isOpen,
  onClose,
  paymentProof,
}) => {

  const dispatch = useDispatch();

  const notify =
    useCustomNotification();

  const [status, setStatus] =
    useState("Pending");

  const [reason, setReason] =
    useState("");


  const BASE_URL =
    process.env.REACT_APP_API_BASE_URL;


  useEffect(() => {

    setStatus(
      paymentProof?.status ||
      "Pending"
    );

    setReason("");

  }, [paymentProof]);


  const handleSave = async () => {

    if (!paymentProof?.payment_id) {

      notify({
        message:
          "Payment Proof Missing",
        description:
          "Unable to identify the payment proof.",
        type: "error",
      });

      return;
    }


    if (!reason.trim()) {

      notify({
        message:
          "Reason Required",
        description:
          "Please provide a reason for the status change.",
        type: "error",
      });

      return;
    }


    const response =
      await dispatch(
        updateStarterKitPaymentProofAction(
          paymentProof.payment_id,
          status,
          reason
        )
      );


    if (response.success) {

      notify({
        message:
          "Payment Proof Updated",
        description:
          `Payment status changed to "${status}".`,
        type: "success",
      });


      dispatch(
        fetchStarterKitPaymentProofsAction(
          1,
          10
        )
      );

      onClose();

    } else {

      notify({
        message:
          "Failed to Update Payment Proof",
        description:
          response.error ||
          "Something went wrong while updating the payment proof.",
        type: "error",
      });

    }
  };


  const document =
    paymentProof?.documents?.[0];


  const documentUrl =
    document?.file_path
      ? `${BASE_URL?.replace(/\/$/, "")}/${document.file_path.replace(/^\//, "")}`
      : null;


  return (

    <ReusableDrawer
      isOpen={isOpen}
      onClose={onClose}

      headerContent={
        <h2>
          Update Payment Proof
        </h2>
      }

      bodyContent={

        <Stack spacing={5}>

          {/* Payment Reference */}

          <Stack spacing={1}>

            <Text
              fontSize="sm"
              color="gray.500"
            >
              Payment Reference
            </Text>

            <Text fontWeight="600">
              {
                paymentProof?.reference ||
                "N/A"
              }
            </Text>

          </Stack>


          {/* Amount */}

          <Stack spacing={1}>

            <Text
              fontSize="sm"
              color="gray.500"
            >
              Amount
            </Text>

            <Text fontWeight="600">
              P
              {Number(
                paymentProof?.amount ||
                  0
              ).toLocaleString()}
            </Text>

          </Stack>


          {/* Payment Proof Document */}

          <Stack spacing={2}>

            <Text
              fontSize="sm"
              fontWeight="600"
              color="gray.500"
            >
              Payment Proof
            </Text>


            {documentUrl ? (

              <Box
                borderWidth="1px"
                borderRadius="lg"
                overflow="hidden"
                bg="gray.50"
                p={3}
              >

                <Image
                  src={documentUrl}
                  alt="Starter Kit Payment Proof"
                  width="100%"
                  maxH="300px"
                  objectFit="contain"
                  borderRadius="md"
                />

                <Text
                  fontSize="xs"
                  color="gray.500"
                  mt={2}
                  textAlign="center"
                >
                  {
                    document.document_type ||
                    "Payment Proof Document"
                  }
                </Text>

              </Box>

            ) : (

              <Box
                p={4}
                borderWidth="1px"
                borderRadius="md"
                bg="gray.50"
              >

                <Text
                  fontSize="sm"
                  color="gray.500"
                >
                  No payment proof document available.
                </Text>

              </Box>

            )}

          </Stack>


          {/* Current Status */}

          <Stack spacing={2}>

            <Text
              fontSize="sm"
              color="gray.500"
            >
              Current Status
            </Text>

            <StatusBadge
              label={
                paymentProof?.status ||
                "Pending"
              }
              variant={
                getStatusVariant(
                  paymentProof?.status ||
                  "Pending"
                )
              }
            />

          </Stack>


          {/* Update Status */}

          <Stack spacing={2}>

            <Text
              fontSize="sm"
              color="gray.500"
            >
              Update Status
            </Text>

            <Select
              value={status}
              onChange={(e) =>
                setStatus(
                  e.target.value
                )
              }
            >

              <option value="Pending">
                Pending
              </option>

              <option value="Verified">
                Verified
              </option>

              <option value="Rejected">
                Rejected
              </option>

            </Select>

          </Stack>


          {/* Status Reason */}

          <ReusableInput
            label="Status Reason"
            placeholder="Provide a reason"
            value={reason}
            onChange={(e) =>
              setReason(
                e.target.value
              )
            }
            type="textarea"
            isRequired
          />

        </Stack>
      }

      footerContent={

        <Stack
          direction="row"
          spacing={4}
          justifyContent="flex-end"
        >

          <GradientButton
            width="100px"
            height="40px"
            onClick={onClose}
          >
            Cancel
          </GradientButton>


          <GradientButton
            width="100px"
            height="40px"
            onClick={handleSave}
          >
            Save
          </GradientButton>

        </Stack>

      }
    />

  );
};


export default UpdatePaymentProofDrawer;